import { AuthService, REFRESH_MARGIN_SECONDS, REFRESH_OK, REFRESH_TERMINAL } from "./authservice";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../config/constants";

// Refresco PROACTIVO del access token. El reactivo (401 → canje) sigue como red
// de seguridad, pero con access de 1 h los streams, sockets y fetch directos no
// pasan por ningún interceptor: si el token vence en silencio, fallan.
//
// El canje real (single-flight por pestaña + lock entre pestañas) vive en
// AuthService.refreshSession; acá solo se decide CUÁNDO.

const MIN_DELAY_MS = 30 * 1000;
// setTimeout usa un entero de 32 bits: un delay mayor (p. ej. un access viejo de
// 300 días) dispararía de inmediato. Se acota y se reevalúa al vencer.
const MAX_DELAY_MS = 6 * 60 * 60 * 1000;
const RETRY_BASE_MS = 5 * 1000;
const RETRY_MAX_MS = 5 * 60 * 1000;

/** Backoff exponencial con jitter completo, acotado. `attempt` arranca en 1. */
export function retryDelayMs(attempt, random = Math.random) {
  const ceiling = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (attempt - 1));
  return Math.max(1000, Math.floor(random() * ceiling));
}

/** Ms hasta el momento de renovar (`exp` - 5 min), con piso de 30 s. null si no hay access legible. */
export function computeRefreshDelayMs(nowMs = Date.now()) {
  const exp = AuthService.getAccessTokenExpiry();
  if (exp === null) return null;
  // +1 s: el chequeo de vigencia es estricto (`ahora > exp - margen`) y a segundo
  // exacto aún cuenta como vigente; sin esto el timer dispararía y no renovaría.
  const delay = (exp - REFRESH_MARGIN_SECONDS) * 1000 - nowMs + 1000;
  return Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, delay));
}

/**
 * Arranca el scheduler. Devuelve `stop()`.
 *
 * @param {object} opts
 * @param {(info: {reason?: string, code?: string}) => void} opts.onSessionEnded
 *   El servidor rechazó la credencial (terminal), o otra pestaña cerró sesión.
 *   Quien llama limpia el estado de UI y redirige.
 * @param {() => void} [opts.onTokensChanged] Hay tokens nuevos: los dejó otra pestaña o esta misma los renovó.
 */
export function startSessionKeeper({ onSessionEnded, onTokensChanged } = {}) {
  let timer = null;
  let stopped = false;
  let attempt = 0;
  let running = false;

  const clear = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const arm = (delayMs) => {
    clear();
    if (stopped) return;
    timer = setTimeout(tick, delayMs);
  };

  const schedule = () => {
    const delay = computeRefreshDelayMs();
    if (delay === null) {
      // Sin access legible pero con refresh: intentar renovar pronto en vez de quedar mudo.
      if (AuthService.getRefreshTokenApi()) arm(MIN_DELAY_MS);
      return;
    }
    arm(delay);
  };

  // Renueva si el access ya está dentro del margen; si no, solo reprograma. Lo usan
  // el timer y los eventos (visibility/online), porque los timers se suspenden con
  // la pestaña dormida o sin red.
  async function tick() {
    if (stopped || running) return;
    running = true;
    try {
      if (AuthService.getFreshAccessTokenSync()) {
        attempt = 0;
        schedule();
        return;
      }
      const result = await AuthService.ensureSession();
      if (stopped) return;
      if (result.status === REFRESH_OK) {
        attempt = 0;
        schedule();
        // El access nuevo trae permisos/rol al día: la UI los re-lee sin recargar.
        onTokensChanged?.();
      } else if (result.status === REFRESH_TERMINAL) {
        AuthService.logout();
        onSessionEnded?.({ reason: result.reason, code: result.code });
      } else {
        // Transitorio: la sesión puede estar viva; reintento con backoff + jitter.
        attempt += 1;
        arm(retryDelayMs(attempt));
      }
    } finally {
      running = false;
    }
  }

  const onVisibility = () => {
    if (document.visibilityState === "visible") tick();
  };
  const onOnline = () => tick();

  const onStorage = (event) => {
    if (event.key !== null && event.key !== ACCESS_TOKEN && event.key !== REFRESH_TOKEN) return;
    const loggedOut = !localStorage.getItem(ACCESS_TOKEN) && !localStorage.getItem(REFRESH_TOKEN);
    if (loggedOut) {
      // Otra pestaña cerró sesión (o la dio por muerta): esta no debe seguir con un refresh ya inválido.
      clear();
      onSessionEnded?.({ reason: "other-tab" });
      return;
    }
    // Otra pestaña renovó o inició sesión: se adopta lo que dejó y se reprograma.
    attempt = 0;
    schedule();
    onTokensChanged?.();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  window.addEventListener("storage", onStorage);
  schedule();

  return () => {
    stopped = true;
    clear();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("storage", onStorage);
  };
}
